/**
 * Phase 138C — Super-Admin Revenue Operations & Billing Health Dashboard
 *
 * Surfaces 4 boardroom-grade sections derived from Phase 138B dunning audit:
 *   1. Summary tiles — MRR, At-risk MRR, recovery rate, dunning counts
 *   2. Active Dunning Funnel — every academy currently in past_due / suspended
 *   3. Recovery Velocity — rolling window dunning → recovered conversion
 *   4. Cohort Retention Matrix — month-by-month Pro retention
 *
 * Data source: GET /api/admin/billing/health-metrics (super_admin only).
 * Pure read view. Polls every 5 minutes; manual refresh button.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import {
  RefreshCw, AlertTriangle, Lock, TrendingUp, Wallet,
  Activity, ArrowLeft, Clock, Crown,
  Mail, MessageCircle, MousePointerClick, Zap, CheckCircle2,
} from 'lucide-react';

function rupees(paise) {
  if (paise === null || paise === undefined) return '—';
  const inr = (paise || 0) / 100;
  return `₹${inr.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function relativeSince(iso) {
  if (!iso) return '—';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const hrs = ms / 3600000;
    if (hrs < 1) return `${Math.round(ms / 60000)}m ago`;
    if (hrs < 24) return `${Math.round(hrs)}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch { return '—'; }
}

function StatTile({ label, value, sub, accent = 'slate', testid }) {
  const accentMap = {
    slate: 'border-slate-200 bg-white',
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
    indigo: 'border-indigo-200 bg-indigo-50',
  };
  return (
    <div className={`rounded-2xl border p-5 ${accentMap[accent]}`} data-testid={testid}>
      <div className="text-[11px] tracking-[0.18em] font-black uppercase text-slate-500 mb-2">{label}</div>
      <div className="text-3xl font-black text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  if (status === 'past_due') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-800 border border-amber-300">
        <AlertTriangle className="h-3 w-3" /> Past Due
      </span>
    );
  }
  if (status === 'suspended') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-rose-100 text-rose-800 border border-rose-300">
        <Lock className="h-3 w-3" /> Suspended
      </span>
    );
  }
  return <span className="text-xs text-slate-500">{status}</span>;
}

function CohortCell({ retained, pct, month }) {
  if (pct === null || pct === undefined) {
    return <td className="p-3 text-center text-xs text-slate-300">—</td>;
  }
  // Color-grade based on retention %
  let bg = 'bg-slate-50 text-slate-600';
  if (pct >= 80) bg = 'bg-emerald-100 text-emerald-800 font-bold';
  else if (pct >= 50) bg = 'bg-emerald-50 text-emerald-700';
  else if (pct >= 25) bg = 'bg-amber-50 text-amber-700';
  else if (pct > 0) bg = 'bg-rose-50 text-rose-700';
  return (
    <td className={`p-3 text-center text-xs ${bg}`}>
      <div className="font-bold tabular-nums">{pct.toFixed(1)}%</div>
      <div className="text-[10px] opacity-70 mt-0.5">{retained}/cohort</div>
    </td>
  );
}

function FunnelChannelCard({ channel, data, accent }) {
  const Icon = channel === 'email' ? Mail : channel === 'whatsapp' ? MessageCircle : MousePointerClick;
  const label = channel === 'email' ? 'Email' : channel === 'whatsapp' ? 'WhatsApp' : 'Shared';
  const accentMap = {
    indigo: { border: 'border-indigo-200', bg: 'bg-indigo-50', text: 'text-indigo-900', dot: 'bg-indigo-600' },
    emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-900', dot: 'bg-emerald-600' },
    slate: { border: 'border-slate-200', bg: 'bg-slate-50', text: 'text-slate-900', dot: 'bg-slate-500' },
  };
  const c = accentMap[accent] || accentMap.slate;
  const fmtMin = (m) => {
    if (m === null || m === undefined) return '—';
    if (m < 60) return `${m.toFixed(1)} min`;
    if (m < 1440) return `${(m / 60).toFixed(1)} h`;
    return `${(m / 1440).toFixed(1)} d`;
  };
  return (
    <div
      className={`rounded-2xl border ${c.border} bg-white p-5 relative overflow-hidden`}
      data-testid={`funnel-card-${channel}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${c.text}`} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.18em] font-black uppercase text-slate-400">Channel</div>
          <div className="text-sm font-black text-slate-900">{label} Performance</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Sent</div>
          <div className="text-xl font-black text-slate-900 tabular-nums">{data?.dispatched ?? 0}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Opened</div>
          <div className="text-xl font-black text-slate-900 tabular-nums">{data?.opened ?? 0}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Paid</div>
          <div className="text-xl font-black text-slate-900 tabular-nums">{data?.paid ?? 0}</div>
        </div>
      </div>
      <div className="space-y-2 border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500 inline-flex items-center gap-1.5"><MousePointerClick className="h-3 w-3" /> Open Rate</span>
          <span className="font-black text-slate-900 tabular-nums" data-testid={`funnel-${channel}-open-rate`}>{(data?.open_rate_pct ?? 0).toFixed(1)}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500 inline-flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3" /> Click → Pay</span>
          <span className="font-black text-emerald-700 tabular-nums" data-testid={`funnel-${channel}-click-to-pay`}>{(data?.click_to_pay_pct ?? 0).toFixed(1)}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500 inline-flex items-center gap-1.5"><TrendingUp className="h-3 w-3" /> Overall Conversion</span>
          <span className="font-black text-slate-900 tabular-nums" data-testid={`funnel-${channel}-overall`}>{(data?.overall_conversion_pct ?? 0).toFixed(1)}%</span>
        </div>
        <div className="flex items-center justify-between text-xs border-t border-slate-100 pt-2 mt-2">
          <span className="text-slate-500 inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> Time to Open</span>
          <span className="font-bold text-slate-700 tabular-nums">{fmtMin(data?.median_time_to_open_minutes)}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500 inline-flex items-center gap-1.5"><Zap className="h-3 w-3" /> Time to Pay</span>
          <span className="font-bold text-slate-700 tabular-nums">{fmtMin(data?.median_time_to_pay_minutes)}</span>
        </div>
      </div>
    </div>
  );
}

function FunnelHUD({ funnel, windowDays }) {
  if (!funnel) return null;
  const overall = funnel.overall || {};
  const channels = funnel.per_channel || {};
  // Identify the winning channel for the highlight banner
  const winner = (() => {
    const candidates = Object.entries(channels)
      .filter(([, d]) => d?.dispatched > 0);
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => (b[1].overall_conversion_pct || 0) - (a[1].overall_conversion_pct || 0));
    return candidates[0];
  })();
  const winnerName = winner?.[0];
  const winnerData = winner?.[1];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="funnel-hud-card">
      <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] tracking-[0.18em] font-black uppercase text-slate-500 flex items-center gap-1.5">
            <MousePointerClick className="h-3.5 w-3.5" /> Magic-Link Funnel · Phase 138F
          </div>
          <div className="text-sm text-slate-700 mt-0.5">Per-channel conversion attribution over last {windowDays} days</div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="text-right">
            <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Overall Conversion</div>
            <div className="text-xl font-black text-slate-900 tabular-nums">{(overall.overall_conversion_pct || 0).toFixed(1)}%</div>
          </div>
          <div className="text-right border-l border-slate-200 pl-3">
            <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Links Dispatched</div>
            <div className="text-xl font-black text-slate-900 tabular-nums">{overall.dispatched || 0}</div>
          </div>
        </div>
      </div>

      {/* Winner banner */}
      {winner && winnerData.dispatched >= 3 && (
        <div className="px-5 py-3 bg-gradient-to-r from-emerald-50 to-white border-b border-emerald-100 text-xs">
          <span className="font-black uppercase tracking-wider text-emerald-800">🏆 Best converting channel:</span>
          <span className="ml-2 font-bold text-slate-900 capitalize">{winnerName}</span>
          <span className="text-slate-500"> · </span>
          <span className="text-slate-700 font-bold">{winnerData.overall_conversion_pct.toFixed(1)}% overall</span>
          <span className="text-slate-400"> ({winnerData.paid}/{winnerData.dispatched} paid)</span>
        </div>
      )}

      {overall.dispatched === 0 ? (
        <div className="p-10 text-center text-sm text-slate-500" data-testid="funnel-hud-empty">
          <Mail className="h-6 w-6 mx-auto mb-2 text-slate-300" />
          No magic links dispatched in this window yet. Once the daily triage fires on a past-due academy, conversion metrics will appear here.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 p-5">
          <FunnelChannelCard channel="email" data={channels.email} accent="indigo" />
          <FunnelChannelCard channel="whatsapp" data={channels.whatsapp} accent="emerald" />
          {channels.shared && (
            <div className="md:col-span-2">
              <FunnelChannelCard channel="shared" data={channels.shared} accent="slate" />
              <div className="text-[10px] text-slate-400 mt-1 px-2">
                "Shared" buckets contain legacy multi-channel rows (pre-138F refactor). Will phase out as new dispatches accumulate.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BillingHealthDashboard() {
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cohortMonths, setCohortMonths] = useState(6);
  const [windowDays, setWindowDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [healthRes, funnelRes] = await Promise.all([
        api.get(`/admin/billing/health-metrics?cohort_months=${cohortMonths}&recovery_window_days=${windowDays}`),
        api.get(`/admin/billing/funnel-metrics?days=${windowDays}`),
      ]);
      setData(healthRes.data);
      setFunnel(funnelRes.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load billing health');
    } finally {
      setLoading(false);
    }
  }, [cohortMonths, windowDays]);

  useEffect(() => {
    fetchData();
    const handle = setInterval(fetchData, 300000); // 5-min auto-refresh
    return () => clearInterval(handle);
  }, [fetchData]);

  const summary = data?.summary;
  const dunningFunnel = data?.dunning_funnel;
  const velocity = data?.recovery_velocity;
  const cohort = data?.cohort_matrix;

  const headers = useMemo(() => cohort?.headers || ['M1', 'M2', 'M3', 'M4', 'M5'], [cohort]);

  return (
    <div className="min-h-screen bg-slate-50 p-6 sm:p-8" data-testid="billing-health-dashboard">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Link to="/super-admin" className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 mb-2">
              <ArrowLeft className="h-3.5 w-3.5" /> Super Admin
            </Link>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight" data-testid="billing-health-title">
              Revenue Operations & Billing Health
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Real-time dunning recovery audit + maturity cohort retention. Data refreshes every 5 minutes.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={windowDays}
              onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
              data-testid="billing-window-selector"
              className="px-3 py-2 text-xs font-bold rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <select
              value={cohortMonths}
              onChange={(e) => setCohortMonths(parseInt(e.target.value, 10))}
              data-testid="billing-cohort-selector"
              className="px-3 py-2 text-xs font-bold rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              <option value={3}>3-month cohorts</option>
              <option value={6}>6-month cohorts</option>
              <option value={12}>12-month cohorts</option>
            </select>
            <button
              onClick={fetchData}
              data-testid="billing-refresh-btn"
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 transition disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border-2 border-rose-200 bg-rose-50 p-5 text-sm text-rose-800" data-testid="billing-health-error">
            <strong className="font-black">Failed to load:</strong> {error}
          </div>
        )}

        {!data && loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="billing-health-loading">
            <RefreshCw className="h-6 w-6 mx-auto mb-3 animate-spin text-slate-400" />
            Aggregating dunning + retention metrics…
          </div>
        )}

        {summary && (
          <>
            {/* Summary Tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="billing-summary-tiles">
              <StatTile
                label="MRR (Active)"
                value={rupees(summary.mrr_inr_paise)}
                sub={`${summary.pro_active_count} Pro academies`}
                accent="emerald"
                testid="tile-mrr-active"
              />
              <StatTile
                label="At-Risk MRR"
                value={rupees(summary.mrr_at_risk_inr_paise)}
                sub={`${summary.pro_at_risk_count} academies · ${summary.at_risk_pct.toFixed(1)}% of MRR`}
                accent={summary.mrr_at_risk_inr_paise > 0 ? 'rose' : 'slate'}
                testid="tile-mrr-at-risk"
              />
              <StatTile
                label={`${velocity?.window_days}d Recovery Rate`}
                value={`${summary.rolling_recovery_rate_pct.toFixed(1)}%`}
                sub={`${velocity?.recovered}/${velocity?.entered} dunning events recovered`}
                accent={summary.rolling_recovery_rate_pct >= 70 ? 'emerald' : (summary.rolling_recovery_rate_pct >= 40 ? 'amber' : 'rose')}
                testid="tile-recovery-rate"
              />
              <StatTile
                label="In Dunning"
                value={summary.total_in_dunning}
                sub={`${summary.current_past_due_count} past due · ${summary.current_suspended_count} suspended`}
                accent={summary.total_in_dunning > 0 ? 'amber' : 'emerald'}
                testid="tile-in-dunning"
              />
            </div>

            {/* Two-up: Dunning Funnel + Recovery Velocity */}
            <div className="grid lg:grid-cols-3 gap-4">
              {/* Dunning Funnel — wide */}
              <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="dunning-funnel-card">
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] tracking-[0.18em] font-black uppercase text-slate-500 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" /> Active Dunning Funnel
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">Academies currently in past_due or suspended</div>
                  </div>
                  <div className="text-2xl font-black text-slate-900 tabular-nums">{dunningFunnel?.total_in_dunning ?? 0}</div>
                </div>
                {dunningFunnel?.rows?.length > 0 ? (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left bg-slate-50/30 text-slate-600 font-bold border-b border-slate-100">
                        <th className="px-5 py-2.5">Academy</th>
                        <th className="px-3 py-2.5">Status</th>
                        <th className="px-3 py-2.5">Plan</th>
                        <th className="px-3 py-2.5">Since</th>
                        <th className="px-5 py-2.5 text-right">Owed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {dunningFunnel.rows.map((row) => (
                        <tr key={row.academy_id} className="hover:bg-slate-50 transition" data-testid={`dunning-row-${row.academy_id}`}>
                          <td className="px-5 py-3 font-bold text-slate-900">{row.academy_name}</td>
                          <td className="px-3 py-3"><StatusPill status={row.status} /></td>
                          <td className="px-3 py-3 text-slate-600">{row.plan}/{row.billing_cycle}</td>
                          <td className="px-3 py-3 text-slate-500">
                            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {relativeSince(row.since)}</span>
                          </td>
                          <td className="px-5 py-3 text-right font-bold text-slate-900 tabular-nums">{rupees(row.unpaid_inr_paise)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-10 text-center text-sm text-slate-500" data-testid="dunning-funnel-empty">
                    <Crown className="h-6 w-6 mx-auto mb-2 text-emerald-400" />
                    No academies in dunning. Every Pro subscription is current.
                  </div>
                )}
              </div>

              {/* Recovery Velocity Card */}
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="recovery-velocity-card">
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                  <div className="text-[11px] tracking-[0.18em] font-black uppercase text-slate-500 flex items-center gap-1.5">
                    <Activity className="h-3.5 w-3.5" /> Recovery Velocity
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5">Rolling {velocity?.window_days}-day window</div>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <div className="text-xs text-slate-500">Dunning events entered</div>
                    <div className="text-2xl font-black text-slate-900 tabular-nums">{velocity?.entered ?? 0}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                      <div className="text-[10px] tracking-wider font-bold uppercase text-emerald-700">Recovered</div>
                      <div className="text-xl font-black text-emerald-900 tabular-nums mt-0.5">{velocity?.recovered ?? 0}</div>
                    </div>
                    <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
                      <div className="text-[10px] tracking-wider font-bold uppercase text-amber-700">Still Pending</div>
                      <div className="text-xl font-black text-amber-900 tabular-nums mt-0.5">{velocity?.still_in_dunning ?? 0}</div>
                    </div>
                  </div>
                  {velocity?.median_recovery_hours !== null && velocity?.median_recovery_hours !== undefined && (
                    <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                      Median recovery time:
                      <span className="font-bold text-slate-700 ml-1 tabular-nums">
                        {velocity.median_recovery_hours >= 24
                          ? `${(velocity.median_recovery_hours / 24).toFixed(1)} days`
                          : `${velocity.median_recovery_hours.toFixed(1)} hours`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cohort Retention Matrix */}
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="cohort-matrix-card">
              <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[11px] tracking-[0.18em] font-black uppercase text-slate-500 flex items-center gap-1.5">
                    <TrendingUp className="h-3.5 w-3.5" /> Maturity Cohort Retention
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5">% of cohort still on healthy Pro plan after N months</div>
                </div>
                <div className="text-xs text-slate-500">Last {cohort?.months_back} cohort months</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left bg-slate-50/30 text-slate-600 font-bold border-b border-slate-100 text-xs">
                      <th className="px-5 py-3">Cohort</th>
                      <th className="px-3 py-3 text-center">Signups</th>
                      {headers.map((h) => (
                        <th key={h} className="px-3 py-3 text-center">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {cohort?.cohorts?.map((c) => (
                      <tr key={c.cohort_month} className="hover:bg-slate-50/50" data-testid={`cohort-row-${c.cohort_month}`}>
                        <td className="px-5 py-3 font-bold text-slate-900">{c.cohort_month}</td>
                        <td className="px-3 py-3 text-center text-slate-600 tabular-nums">{c.signup_count}</td>
                        {c.retention.map((r) => (
                          <CohortCell key={r.month} retained={r.retained} pct={r.pct} month={r.month} />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/30 flex items-center gap-3 flex-wrap text-[10px] text-slate-500">
                <span className="font-bold">Legend:</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-100"></span>≥80%</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-50"></span>50–79%</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-50"></span>25–49%</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-rose-50"></span>1–24%</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-slate-50 border border-slate-200"></span>0% or cohort not matured</span>
              </div>
            </div>

            {/* Phase 138F — Magic-Link Funnel Analytics */}
            <FunnelHUD funnel={funnel} windowDays={windowDays} />

            {/* Footer attribution */}
            <div className="text-[10px] text-slate-400 text-center pt-2">
              <Wallet className="inline h-3 w-3 mr-1" />
              Phase 138C–F · Derived from Phase 138B–E audit trail · Generated {new Date(data.generated_at).toLocaleString()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
